import React from 'react';
import {EzModal, EzField, EzTextStyle} from '@ezcater/recipe';
import styled from '@emotion/styled';
import {type TFunction} from 'i18next';
import LeftArrow from '@src/icons/LeftArrow';
import {SubmitContactUsFormMutationVariables} from '../../../__generated__/SubmitContactUsFormMutation';

interface ErrorsObjectInterface {
  email: boolean;
  message: boolean;
  name: boolean;
}

type EmailModalLayoutProps = {
  errors: ErrorsObjectInterface;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDismiss: () => void;
  onSubmit: () => Promise<void>;
  switchToSupportModal: () => void;
  useMessage: TFunction<'translation', undefined>;
  values: SubmitContactUsFormMutationVariables;
};

const BackToSupportContainer = styled('div')`
  padding-bottom: ${({theme}) => theme.recipeSpacing.md};

  > a {
    cursor: pointer;
  }
`;

const FieldContainer = styled('div')`
  textarea,
  input {
    box-sizing: border-box;
  }

  div:not(:last-child) {
    margin-bottom: ${({theme}) => theme.recipeSpacing.md};
  }
`;

const EmailModalLayout: React.FC<EmailModalLayoutProps> = ({
  errors,
  onChange,
  onDismiss,
  onSubmit,
  switchToSupportModal,
  useMessage,
  values,
}) => {
  const backToSupport = useMessage('components.SupportModal.EmailModal.backToSupport');
  const dismissLabel = useMessage('components.SupportModal.EmailModal.dismiss');
  const email = useMessage('components.SupportModal.EmailModal.email');
  const emailError = useMessage('components.SupportModal.EmailModal.emailError');
  const headerText = useMessage('components.SupportModal.EmailModal.header');
  const message = useMessage('components.SupportModal.EmailModal.message');
  const messageError = useMessage('components.SupportModal.EmailModal.messageError');
  const name = useMessage('components.SupportModal.EmailModal.name');
  const nameError = useMessage('components.SupportModal.EmailModal.nameError');
  const submitLabel = useMessage('components.SupportModal.EmailModal.submit');

  const handleBackToSupport = (
    event: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>
  ) => {
    switchToSupportModal();
    event.preventDefault();
  };

  /* eslint-disable jsx-a11y/anchor-is-valid */
  /* eslint-disable jsx-a11y/no-static-element-interactions */
  // the a11y warnings are for the "back to support" link. They're 100% correct this
  // should be a button. I just don't have time to port it over when copying the existing
  // implementation from ezmanage-ui.
  // bug card: https://ezcater.atlassian.net/browse/EZMNG-213

  return (
    <EzModal
      dismissLabel={dismissLabel}
      headerText={headerText}
      isOpen
      onDismiss={onDismiss}
      onSubmit={onSubmit}
      submitLabel={submitLabel}
    >
      <BackToSupportContainer>
        <a onClick={handleBackToSupport} onKeyPress={handleBackToSupport}>
          <LeftArrow /> <EzTextStyle use="strong">{backToSupport}</EzTextStyle>
        </a>
      </BackToSupportContainer>
      <FieldContainer>
        <EzField
          error={errors.name && nameError}
          label={name}
          name={name}
          onChange={onChange}
          touched
          type="text"
          value={values.name || ''}
        />
        <EzField
          error={errors.email && emailError}
          label={email}
          name={email}
          onChange={onChange}
          touched
          type="text"
          value={values.email || ''}
        />
        <EzField
          error={errors.message && messageError}
          label={message}
          name={message}
          onChange={onChange}
          touched
          type="textarea"
          value={values.message || ''}
        />
      </FieldContainer>
    </EzModal>
  );
  /* eslint-enable jsx-a11y/no-static-element-interactions */
  /* eslint-enable jsx-a11y/anchor-is-valid */
};

export default EmailModalLayout;
